import db from './db-service.js';
import { reportError, toErrorString } from '../utils/errorLogger.js';
import { stripNul, symDecrypt, symEncrypt, tryGetSessionDEK } from '../helper/dekManager.js';

/**
 * at-rest.js — local-only encryption for the Dexie stores that are NOT mail tables.
 *
 * db/indexed-helper.js encrypted the mail cache at rest so it could safely SURVIVE a sign-in
 * (routes/SignIn.js no longer wipes the database for the same user). Three stores were left behind on
 * the v1 plaintext schema, and the sign-in wipe used to be the only thing scrubbing them:
 *
 *   - `attachment` — DECRYPTED file bytes.
 *   - `outbox`     — the plaintext body of a mail whose send failed.
 *   - `draft`      — the plaintext body of a mail still being composed.
 *
 * `organizationData` (the user's primaryEmail + organization/user settings) joined them later, for the
 * same reason: the wipe had been the only thing scrubbing it.
 *
 * They now use the same envelope as the mail cache: ciphertext under the dMail DEK
 * (helper/dekManager.js), tagged with `cacheVersion` and owner-tagged with `userName`. Same
 * compatibility rules as indexed-helper.js:
 *
 *  - Rows without a cacheVersion are legacy plaintext. They are still READ, and are transparently
 *    rewritten as ciphertext on the way out, so existing installs converge without losing anything. A
 *    legacy row must never throw.
 *  - Rows are owner-tagged, so one account's data is never served to another. Ownerless legacy rows are
 *    treated as the current user's, which is safe because sign-in still wipes on an account switch.
 *
 * Unlike the mail cache, drafts and outbox are the user's OWN composed content and the ONLY copy of it —
 * there is no wallet path to fall back to. So when the DEK cannot be resolved these writers report
 * failure to the caller (which surfaces it) rather than silently dropping the mail. What they never do is
 * fall back to writing plaintext.
 *
 * Encrypting these costs zero extra wallet prompts: the DEK is unwrapped once per session and this is
 * content the user already holds in the clear.
 */

/** Per-ROW cache discriminator. NOT a crypto version — never pass this to the WASM (see dekManager). */
export const CACHE_VERSION = 'v2';

export function currentUser() {
  try {
    return localStorage.getItem('UserName') || '';
  } catch {
    return '';
  }
}

/** Legacy (ownerless) rows count as the current user's — see the owner-tagging note above. */
const isOwnedBy = (row, user) => !row?.userName || row.userName === user;

const isEncrypted = (row) => row?.cacheVersion === CACHE_VERSION;

function report(fn, error) {
  try {
    reportError({
      endpoint: '-',
      message: `FileName: at-rest.js, Fun: ${fn}, ${String(error?.message || 'Unhandled error')}`,
      error: toErrorString(error || error?.message),
      userName: currentUser(),
    });
  } catch {
    /* reporting must never break the caller */
  }
}

/* ------------------------------------------------------------------ *
 * Row envelope
 * ------------------------------------------------------------------ */

/** Build the encrypted column value for an arbitrary JSON-able value. */
async function sealJson(dek, value) {
  return await symEncrypt(dek, JSON.stringify(value));
}

/** Reverse of sealJson. Returns null when the blob cannot be opened. */
async function openJson(dek, ciphertext) {
  const plain = stripNul(await symDecrypt(dek, ciphertext));
  return plain ? JSON.parse(plain) : null;
}

/**
 * Read the `json` column of an outbox/draft row.
 *
 * A legacy row's `json` is a plain object and is returned as-is, then rewritten encrypted in the
 * background. Returns null when the row belongs to nobody readable (no DEK / corrupt blob) — callers
 * drop it from the list rather than showing a broken entry.
 */
async function readJsonRow(tableName, row) {
  try {
    if (!isEncrypted(row)) {
      const legacy = row?.json ?? null;
      if (legacy) rewriteLegacyJsonRow(tableName, row, legacy);
      return legacy;
    }
    const dek = await tryGetSessionDEK(currentUser());
    if (!dek) return null;
    return await openJson(dek, row.json);
  } catch (error) {
    report('readJsonRow', error);
    return null;
  }
}

/** Best-effort re-seal of a legacy plaintext row. Never throws, never blocks the read. */
function rewriteLegacyJsonRow(tableName, row, json) {
  (async () => {
    try {
      const user = currentUser();
      const dek = await tryGetSessionDEK(user);
      if (!dek) return;
      await db.table(tableName).update(row.id, {
        json: await sealJson(dek, json),
        userName: user,
        cacheVersion: CACHE_VERSION,
      });
    } catch (error) {
      report('rewriteLegacyJsonRow', error);
    }
  })();
}

/** Owner-scoped rows of a `json` store, in table order, each decrypted to { id, json }. */
async function listJsonRows(tableName) {
  const user = currentUser();
  const rows = (await db.table(tableName).toArray()).filter((row) => isOwnedBy(row, user));
  const out = [];
  for (const row of rows) {
    const json = await readJsonRow(tableName, row);
    if (json) out.push({ id: row.id, json });
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * outbox
 * ------------------------------------------------------------------ */

/**
 * The signed-in user's outbox, oldest first — the same order the raw table used to be read in.
 *
 * This is the ONE definition of outbox order. It is load-bearing: the outbox is addressed BY POSITION
 * (redux `outBoxIndex`, and deleteOutboxAt below), so a reader that filtered differently from the deleter
 * would delete somebody else's row.
 */
export async function listOutbox() {
  try {
    return await listJsonRows('outbox');
  } catch (error) {
    report('listOutbox', error);
    return [];
  }
}

/**
 * Append a failed send to the outbox. Returns false if it could not be stored ENCRYPTED — the caller has
 * already failed the send at this point, so it reports rather than throwing.
 */
export async function addOutbox(json) {
  try {
    const user = currentUser();
    const dek = await tryGetSessionDEK(user);
    if (!dek) {
      report('addOutbox', new Error('DEK unavailable — refusing to store the outbox mail in plaintext'));
      return false;
    }
    await db.table('outbox').add({
      json: await sealJson(dek, json),
      userName: user,
      cacheVersion: CACHE_VERSION,
    });
    return true;
  } catch (error) {
    report('addOutbox', error);
    return false;
  }
}

/** Delete the outbox entry at `index` of listOutbox() — the order the UI rendered. */
export async function deleteOutboxAt(index) {
  try {
    const rows = await listOutbox();
    const row = rows[index];
    if (!row) return true;
    await db.table('outbox').delete(row.id);
    return true;
  } catch (error) {
    report('deleteOutboxAt', error);
    return false;
  }
}

/* ------------------------------------------------------------------ *
 * draft
 * ------------------------------------------------------------------ */

/** The signed-in user's drafts, oldest first, each as { id, json }. */
export async function listDrafts() {
  try {
    return await listJsonRows('draft');
  } catch (error) {
    report('listDrafts', error);
    return [];
  }
}

/**
 * Create or update a draft. Returns the row id, or null when it could not be stored encrypted — the
 * caller MUST tell the user, because a draft is the only copy of what they typed.
 */
export async function saveDraft(id, json) {
  try {
    const user = currentUser();
    const dek = await tryGetSessionDEK(user);
    if (!dek) {
      report('saveDraft', new Error('DEK unavailable — refusing to store the draft in plaintext'));
      return null;
    }
    const record = {
      json: await sealJson(dek, json),
      userName: user,
      cacheVersion: CACHE_VERSION,
    };
    if (!id) return await db.table('draft').add(record);

    // Dexie's update() is NOT an upsert. When the row is gone — deleted in another tab, or wiped by an
    // account switch — it writes nothing and resolves 0, and returning `id` regardless reported success to
    // ComposeEmail while the ONLY copy of what the user typed was dropped on the floor. Re-add it instead.
    //
    // update() also resolves 0 for a row that exists but did not actually change, so confirm the row is
    // really gone before adding: a blind add() would duplicate the draft in that case. (In practice the
    // sealed `json` differs on every write, but the fallback must not depend on that.) Both steps run in
    // one transaction so a concurrent delete cannot slip between them.
    return await db.transaction('rw', db.table('draft'), async () => {
      const modified = await db.table('draft').update(id, record);
      if (modified) return id;
      if (await db.table('draft').get(id)) return id;
      return await db.table('draft').add(record);
    });
  } catch (error) {
    report('saveDraft', error);
    return null;
  }
}

/* ------------------------------------------------------------------ *
 * organizationData
 * ------------------------------------------------------------------ */

/**
 * The organization row is a cached copy of the /getOrganizationData response, written by
 * components/Header.js on every mount. It carries the user's primaryEmail, the organization's settings and
 * their personal userSettingsData — all plaintext until now, and nothing scrubs it any more since the
 * unconditional sign-in wipe was removed, so it survived indefinitely.
 *
 * The payload is sealed into `data` exactly like the outbox/draft `json` column. `uuid` and
 * `organizationId` deliberately stay in the CLEAR, for the same reason `s3url` does on the attachment
 * store: they are opaque server-issued identifiers, they carry no user content, and helper/txSigner.js
 * resolveTier() reads them to choose the signing path — a decision that must not depend on the DEK being
 * resolvable (a DEK hiccup would otherwise silently demote a sponsored user to paying their own gas).
 *
 * There is at most ONE row (Header upserts, service/api-actions.login() clears the table), so the writer
 * collapses any extras rather than trusting that invariant.
 */

/** The signed-in user's organization payload, or null when there is none / it cannot be opened. */
export async function readOrganizationData() {
  try {
    const user = currentUser();
    const rows = await db.table('organizationData').toArray();
    const row = rows.find((candidate) => isOwnedBy(candidate, user));
    if (!row) return null;

    if (!isEncrypted(row)) {
      // Legacy plaintext row: the server payload was spread across the row itself. Hand it back as it
      // always was, minus Dexie's own key, and re-seal it in the background.
      const { id, ...legacy } = row;
      saveOrganizationData(legacy).catch(() => {});
      return legacy;
    }

    const dek = await tryGetSessionDEK(user);
    if (!dek) return null;
    return await openJson(dek, row.data);
  } catch (error) {
    report('readOrganizationData', error);
    return null;
  }
}

/**
 * Same read, but tells "this user has no organization" apart from "there IS an org row and we could
 * not open it". readOrganizationData() collapses both to null, which is fine for display but NOT for
 * security decisions: before this data was sealed, null could only ever mean "no org", so a caller
 * treating null as unaffiliated was correct. Now a missing DEK produces the same null and silently
 * switches org restrictions OFF. Callers enforcing a restriction must use this and fail closed.
 *
 * Returns null only when the user genuinely has no organization row. Throws when a row exists but
 * cannot be opened.
 */
export async function readOrganizationDataStrict() {
  const user = currentUser();
  const rows = await db.table('organizationData').toArray();
  const row = rows.find((candidate) => isOwnedBy(candidate, user));
  if (!row) return null; // genuinely unaffiliated

  if (!isEncrypted(row)) {
    const { id, ...legacy } = row;
    saveOrganizationData(legacy).catch(() => {});
    return legacy;
  }

  const dek = await tryGetSessionDEK(user);
  if (!dek) throw new Error('organizationData is sealed but no DEK is available to open it');
  return await openJson(dek, row.data);
}

/**
 * Upsert the organization payload, sealed under the DEK. Returns false when it could not be stored
 * ENCRYPTED — like the attachment store this is a cache of a server response, so skipping costs one
 * re-fetch on the next mount; it never falls back to writing plaintext.
 */
export async function saveOrganizationData(value) {
  try {
    if (!value) return false;
    const user = currentUser();
    const dek = await tryGetSessionDEK(user);
    if (!dek) return false;

    const record = {
      data: await sealJson(dek, value),
      userName: user,
      cacheVersion: CACHE_VERSION,
    };
    // Cleartext tier discriminators only — see the note above. Omitted entirely when absent so a free
    // user's row has no uuid key at all, which is what resolveTier() already tests for.
    if (value.uuid) record.uuid = value.uuid;
    if (value.organizationId) record.organizationId = value.organizationId;

    const rows = await db.table('organizationData').toArray();
    if (!rows.length) {
      await db.table('organizationData').add(record);
      return true;
    }
    // put(), not update(): update() MERGES, which would leave a legacy row's plaintext keys sitting
    // alongside the sealed blob — the exact leak this function exists to close.
    await db.table('organizationData').put({ ...record, id: rows[0].id });
    if (rows.length > 1) {
      await db.table('organizationData').bulkDelete(rows.slice(1).map((row) => row.id));
    }
    return true;
  } catch (error) {
    report('saveOrganizationData', error);
    return false;
  }
}

/* ------------------------------------------------------------------ *
 * attachment
 * ------------------------------------------------------------------ */

/**
 * Attachments are binary, and symEncrypt takes/returns strings — so the bytes are base64'd before
 * sealing and rehydrated into a File on the way out. The filename and MIME type travel INSIDE the
 * envelope: they are user data too, and nothing queries them. Only `s3url` (an opaque, server-issued
 * object key) stays in the clear, because it is the lookup key.
 */
async function fileToBase64(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const CHUNK = 0x8000; // apply() on the whole array blows the call stack for large files
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToBytes(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Upsert an attachment row keyed on its s3url, sealed under the DEK.
 *
 * The de-duplication the plaintext version did is preserved exactly: '++id' meant a plain .add() inserted
 * a NEW row every time an email was re-opened, so every row for this s3url is collapsed back down to one.
 * Collapsing across owners is intentional and matches the previous behaviour — s3url identifies the S3
 * object, the row is replaced (never disclosed), and sign-in wipes on an account switch anyway.
 */
export async function putAttachmentFile({ name, s3url, file }) {
  try {
    if (!s3url || !file) return false;
    const user = currentUser();
    const dek = await tryGetSessionDEK(user);
    if (!dek) return false; // a cache: skipping costs one re-fetch, plaintext would cost the file
    const record = {
      s3url,
      file: await sealJson(dek, {
        name: name || file.name || 'attachment',
        type: file.type || '',
        data: await fileToBase64(file),
      }),
      userName: user,
      cacheVersion: CACHE_VERSION,
    };
    const existing = await db.attachment.where('s3url').equals(s3url).toArray();
    if (!existing.length) {
      await db.table('attachment').add(record);
      return true;
    }
    await db.table('attachment').put({ ...record, id: existing[0].id });
    if (existing.length > 1) {
      await db.attachment.bulkDelete(existing.slice(1).map((row) => row.id));
    }
    return true;
  } catch (error) {
    report('putAttachmentFile', error);
    return false;
  }
}

/** The cached File for an s3url, or null on a miss. Legacy plaintext rows are read and re-sealed. */
export async function getAttachmentFile(s3url) {
  try {
    if (!s3url) return null;
    const user = currentUser();
    const rows = await db.attachment.where('s3url').equals(s3url).toArray();
    const row = rows.find((candidate) => isOwnedBy(candidate, user));
    if (!row) return null;

    if (!isEncrypted(row)) {
      // Legacy plaintext row: `file` is a real File/Blob written before this store was encrypted.
      const legacy = row.file || null;
      if (legacy) {
        putAttachmentFile({ name: row.name || legacy.name, s3url: row.s3url, file: legacy }).catch(() => {});
      }
      return legacy;
    }

    const dek = await tryGetSessionDEK(user);
    if (!dek) return null;
    const payload = await openJson(dek, row.file);
    if (!payload?.data) return null;
    return new File([base64ToBytes(payload.data)], payload.name || 'attachment', {
      type: payload.type || 'application/octet-stream',
    });
  } catch (error) {
    report('getAttachmentFile', error);
    return null;
  }
}

/**
 * Which of `keys` are already cached for this user. Index-only (`.keys()` on the compound index), so a
 * prefetch check never loads the attachment bodies it is trying to avoid loading.
 *
 * Legacy rows carry no userName and so are not in the compound index — they read as "not cached", get
 * re-fetched, and are rewritten encrypted by putAttachmentFile. That is the migration path for
 * attachments, and it is why plaintext rows do not linger until someone happens to open them.
 */
export async function getCachedAttachmentKeys(keys) {
  try {
    const wanted = (keys || []).filter(Boolean);
    if (!wanted.length) return new Set();
    const user = currentUser();
    const found = await db.attachment
      .where('[s3url+userName]')
      .anyOf(wanted.map((key) => [key, user]))
      .keys();
    return new Set(found.map(([s3url]) => s3url));
  } catch (error) {
    report('getCachedAttachmentKeys', error);
    return new Set();
  }
}
